import type { QuickRebuttal, SalesChecklist } from "@expert/contentful";

import { Box, Button, Stack, Text } from "@mantine/core";

import type { EligibleProduct } from "../../../../types";

import { useSalesContext } from "../../../../sales-context/SalesContext";
import { SaleProgress } from "../../../../types";
import { FullOfferChecklist } from "../FullOfferChecklist/FullOfferChecklist";
import { LightBulbIcon } from "../Icons";
import { SalesFooter } from "./SalesFooter";
import styles from "./SalesFooter.module.css";

function formatQuickRebuttal(doc?: QuickRebuttal): string {
    if (!doc?.objections?.length) return "";
    const q = (s = "") => `“${s}”`;
    return doc.objections
        .map((objection, i) => {
            const { title, responses = [] } = objection;
            const outerListItem = `${i + 1}. ${title === "Early Rebuttal" ? title : q(title)}`;
            const innerListItems = responses.map((response) => `- ${q(response.text)}`).join("\n");
            return innerListItems ? `${outerListItem}\n${innerListItems}` : outerListItem;
        })
        .join("\n\n");
}

interface FullOfferChecklistStepProps {
    product: EligibleProduct;
    salesChecklist?: SalesChecklist;
    quickRebuttal?: QuickRebuttal;
}

export function FullOfferChecklistStep({ product, salesChecklist, quickRebuttal }: FullOfferChecklistStepProps) {
    const { logger, onQuickRebuttal } = useSalesContext();

    const QuickRebuttalButton = () => (
        <Button
            className={styles.quickRebuttalButton}
            variant="outline"
            radius="lg"
            fz="xs"
            size="sm"
            leftSection={<LightBulbIcon />}
            onClick={() => onQuickRebuttal?.(formatQuickRebuttal(quickRebuttal))}
            color="white"
        >
            Quick rebuttal
        </Button>
    );

    if (!salesChecklist) {
        logger.error({ product, salesChecklist }, "Full offer checklist failed to render");
    }

    return (
        <Stack pos="relative" flex={1}>
            <Box flex={1} style={{ overflowY: "auto" }}>
                {product.saleProgress === SaleProgress.InProgress && salesChecklist ? (
                    <FullOfferChecklist salesChecklist={salesChecklist} />
                ) : (
                    <Text mt="20%" c="dark.1" size="sm">
                        Offer checklist is currently unavailable. Try again later
                    </Text>
                )}
            </Box>
            <SalesFooter product={product} buttonSlot={quickRebuttal && <QuickRebuttalButton />} />
        </Stack>
    );
}
